import { useState } from 'react'
import { Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { downloadXlsReport } from '../api'
import { useHomeStore } from '../store'
import type { CompanyValues, CoverageValues, Vehicle } from '../types'

export function DownloadButton() {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const general = useHomeStore((s) => s.general)
  const vehicles = useHomeStore((s) => s.vehicles)

  async function onClick() {
    setError(null)
    setBusy(true)
    try {
      const payload = buildPayload(general, vehicles)
      const { blob, filename } = await downloadXlsReport(payload)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.rel = 'noopener'
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Stažení selhalo.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col items-center gap-2">
      <Button onClick={onClick} disabled={busy} variant="default" size="lg" className="bg-orange-500 hover:bg-orange-600">
        <Download className="mr-2 h-4 w-4" />
        {busy ? 'Stahuji…' : 'Stáhnout data'}
      </Button>
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  )
}

function buildPayload(general: ReturnType<typeof useHomeStore.getState>['general'], vehicles: Vehicle[]) {
  const pojistnik = general.pojistnik as CompanyValues
  const vlastnik = general.ownerSameAsPolicyholder ? pojistnik : general.vlastnik
  const provozovatel = general.operatorSameAsPolicyholder ? pojistnik : general.provozovatel

  return {
    general: {
      pojistnik,
      vat: general.vat,
      vlastnik,
      provozovatel,
      insurance_start_date: general.insurance_start_date,
      uziti_vozidla: general.uziti_vozidla,
      dalsi_informace: general.dalsi_informace,
      pojistne_kryti: general.pojistne_kryti,
    },
    cars: vehicles.map((v) => {
      const data = v.car_gov_data?.Data
      const coverage: CoverageValues = v.insurance ?? general.pojistne_kryti
      const ownerSame = v.ownerSameAsPolicyholder !== false
      const operatorSame = v.operatorSameAsPolicyholder !== false
      // "Same as policyholder" must export the policyholder explicitly. Sending
      // null makes the report fall back to the GLOBAL owner, which is wrong when
      // a car is individualised back to the policyholder while the global owner
      // differs (e.g. Škofin).
      const vlastnik = ownerSame ? pojistnik : (v.owner_company ?? null)
      const provozovatel = operatorSame ? pojistnik : (v.operator_company ?? null)
      return {
        vin: v.vin,
        spz: v.spz ?? '',
        najete_km: v.najete_km ?? '',
        hodnota_vozidla: v.hodnota_vozidla ?? '',
        insurance_start_date: v.insurance_start_date ?? general.insurance_start_date,
        uziti_vozidla: v.uziti_vozidla ?? general.uziti_vozidla,
        owner_ico: vlastnik?.COMPANY_REGISTRATION_NUMBER ?? null,
        vlastnik,
        operator_ico: provozovatel?.COMPANY_REGISTRATION_NUMBER ?? null,
        provozovatel,
        dalsi_informace: v.dalsi_informace ?? '',
        pojistne_kryti: coverage,
        tp_orv: `${data?.CisloTp ?? ''} / ${data?.CisloOrv ?? ''}`,
        car_gov_data: v.car_gov_data ?? null,
        specifikace:
          v.specifikace ||
          (data?.Specifikace as string) ||
          (data?.VozidloDruh2R as string) ||
          (data?.VozidloDruh2 as string) ||
          (data?.DruhVozidla2R as string) ||
          (data?.DruhVozidla2 as string) ||
          '',
      }
    }),
  }
}
